import { store, computed, observable, action, event } from '~/common/mobx.decorator'
import { routerStore } from '~/stores'
import { HomeSvg, ViewAsTravelerSvg, JobsSvg, TravelersSvg, MyJobsSvg, TimecardNavSvg } from '~/components/icons'
import { EVENTS, PATHS } from '~/common/constants'
import { apolloClient, GET_RECRUITER_STATISTICS_QUERY } from '~/common/apollo'
import { eventBus } from 'mobx-event-bus2'

const FEATURE_TYPE = {
	default: 'default',
	common: 'common',
}

@store()
class CareStore {
	@observable showProfileSidebar
	@observable customHeader = {}
	@observable statistics
	@observable customBreadcumbs = {}

	@action
	fetchStatistics = async () => {
		const response = await apolloClient.query({
			query: GET_RECRUITER_STATISTICS_QUERY,
		})
		this.statistics = response.data.recruiterStatistics
	}

	getFeature = (type) => {
		const defaultMenu = [
			{
				code: 'recruiter-home',
				name: 'DASHBOARD',
				path: PATHS.recruiter.home,
				icon: HomeSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				eventId: 'dashboard-nav_btn',
			},
			{
				// Kept registered, off the sidebar: the feature is now nothing but redirects into
				// Candidate Detail, which every one of its tabs duplicated. Removing the entry entirely
				// would 404 the links people have bookmarked.
				code: 'recruiter-view-as-traveler',
				name: 'VIEW_AS_TRAVELER',
				path: PATHS.recruiter.view_as_traveler,
				icon: ViewAsTravelerSvg,
				auth: true,
				showAppBar: true,
				showSideBar: false,
				useLayout: true,
				eventId: 'recruiter-view-as-nav_btn',
			},
			{
				code: 'recruiter-candidate-detail',
				name: 'CANDIDATE_DETAIL',
				path: PATHS.recruiter.candidates,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
			},
			{
				code: 'recruiter-jobs',
				name: 'JOBS',
				path: PATHS.recruiter.recruiter_jobs,
				icon: JobsSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				statistic: this.statistics?.activeJobTotal,
				eventId: 'jobs-nav_btn',
			},
			{
				code: 'care-timecards',
				name: 'TIMECARDS',
				path: PATHS.recruiter.timecards,
				icon: TimecardNavSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
				eventId: 'timecards-nav_btn',
			},
			{
				// Routed but not in the sidebar: it is reached from the account menu, the way an account
				// page normally is. The portal signs in through Salesforce SSO only, so there is no
				// password to change — this replaces the Change Password entry entirely.
				code: 'recruiter-profile',
				name: 'PROFILE',
				path: PATHS.recruiter.profile,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
			},
			{
				code: 'recruiter-travelers',
				name: 'Travelers',
				path: PATHS.recruiter.travelers,
				icon: TravelersSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				statistic: this.statistics?.workerTotal,
				eventId: 'travelers-nav_btn',
			},
			{
				code: 'care-jobs',
				name: 'JOBS',
				path: PATHS.recruiter.jobs,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				code: 'recruiter-job-info',
				name: 'JOBS',
				path: PATHS.recruiter.job_info,
				breadcrumbPath: PATHS.recruiter.recruiter_jobs,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				// Routed but off the sidebar: /toas is now a redirect into the Candidates page's On
				// Assignment tab. Keeping a second screen for the same list meant two sets of columns
				// to size, two settings panels and two places for a bug like the em-dash rows to hide.
				code: 'recruiter-toas',
				name: 'ON_ASSIGNMENT',
				path: PATHS.recruiter.toas,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				code: 'care-popular-cities',
				name: 'POPULAR_CITIES',
				path: PATHS.recruiter.popular_cities,
				icon: MyJobsSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				code: 'care-suggested-jobs',
				name: 'SUGGESTED_JOBS',
				path: PATHS.recruiter.suggested_jobs,
				icon: MyJobsSvg,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				code: 'recruiter-job-applicants',
				name: 'JOB_APPLICANTS',
				path: PATHS.recruiter.job_applicants,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
		].filter(Boolean)

		const common = [
			{
				code: 'common-signin',
				name: 'SIGNIN',
				path: PATHS.common.signin,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'common-sso',
				name: 'SINGLE_SIGN_ON',
				path: PATHS.recruiter.single_sign_on,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'common-salesforce-callback',
				name: 'SALESFORCE_CALLBACK',
				path: PATHS.recruiter.salesforce_callback,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'common-reset',
				name: 'RESET',
				path: PATHS.common.reset,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
			},
			{
				code: 'common-change-password',
				name: 'CHANGE_PASSWORD',
				path: PATHS.common.change_password,
				icon: 'user',
				// Signed-in only, and the code is already in authStore's allowed feature list. Rendered
				// inside the shell so the page keeps its title and the sidebar to get back out —
				// without the layout it was a bare form floating on a blank page.
				auth: true,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: true,
			},
			{
				code: 'common-errors',
				name: 'ERRORS',
				path: PATHS.common.errors,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
			},
		].filter(Boolean)

		let result = null
		switch (type) {
			case FEATURE_TYPE.default:
				result = defaultMenu
				break
			case FEATURE_TYPE.common:
				result = common
				break
			default:
				result = []
				break
		}
		return result
	}

	@computed
	get features() {
		const defaultMenu = this.getFeature(FEATURE_TYPE.default)

		return [...defaultMenu]
	}

	@computed
	get allFeatures() {
		const common = this.getFeature(FEATURE_TYPE.common)
		const allFeature = [...this.features, ...common]

		return allFeature?.find((feature) => feature.path === routerStore.basePath)
	}

	@computed
	get activeFeature() {
		return this.features?.find((feature) => feature.path === routerStore.basePath)
	}

	@computed
	get code() {
		return this.activeFeature?.code
	}

	@computed
	get name() {
		return this.activeFeature?.name || ''
	}

	@computed
	get useLayout() {
		return this.activeFeature?.useLayout
	}

	@computed
	get sidebarMenu() {
		return this.features?.filter((feature) => feature.showSideBar)
	}

	@computed
	get appbarMenu() {
		return this.features?.filter((feature) => feature.showAppBar)
	}

	@action
	toogleProfileSidebar = async () => {
		this.showProfileSidebar = !this.showProfileSidebar
	}

	@action
	logout = async () => {
		this.showProfileSidebar = false

		// const response = await apolloClient.mutate({
		//   mutation: SIGNOUT_RECRUITER_MUTATION,
		// })

		// const success = response.data?.signOutRecruiter
		// if(success) notifyStore.success("$MESSAGES.SUCCESSFUL")

		eventBus.post(EVENTS.authStore.logout)
		routerStore.goPage(PATHS.recruiter.single_sign_on)
	}

	@event(EVENTS.careStore.updateHeader)
	async onUpdateHeader({ payload }) {
		this.customHeader = payload
	}

	@event(EVENTS.careStore.updateBreadcumbs)
	async onUpdateBreadcumbs({ payload }) {
		this.customBreadcumbs = payload
	}
}

export const careStore = new CareStore()
